import React, { useState, useEffect, useMemo } from "react";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";

import {
  FiPlus,
  FiEye,
  FiSearch,
  FiUser,
  FiDollarSign,
  FiCalendar,
  FiChevronsUp,
  FiChevronsDown,
  FiFilter,
  FiCreditCard,
  FiAlertTriangle,
  FiList,
  FiChevronLeft,
  FiChevronRight,
  FiFileText,
} from "react-icons/fi";

// --- MOCK DATA / UTILS ---
const MOCK_INVOICES = [
  { id: "INV-001", customerName: "Acme Corp", invoiceDate: "2025-12-01", dueDate: "2025-12-16", grandTotal: 2000.0, status: "Paid" },
  { id: "INV-002", customerName: "Globex Solutions", invoiceDate: "2025-11-25", dueDate: "2025-12-10", grandTotal: 1550.5, status: "Overdue" },
  { id: "INV-003", customerName: "Monolith Tech", invoiceDate: "2025-12-05", dueDate: "2025-12-20", grandTotal: 4500.75, status: "Pending" },
  { id: "INV-004", customerName: "Acme Corp", invoiceDate: "2025-12-08", dueDate: "2025-12-23", grandTotal: 980.0, status: "Draft" },
  { id: "INV-005", customerName: "Stark Industries", invoiceDate: "2025-10-15", dueDate: "2025-10-30", grandTotal: 12000.0, status: "Paid" },
  { id: "INV-006", customerName: "Globex Solutions", invoiceDate: "2025-11-01", dueDate: "2025-11-15", grandTotal: 300.0, status: "Overdue" },
  { id: "INV-007", customerName: "Monolith Tech", invoiceDate: "2025-12-09", dueDate: "2025-12-24", grandTotal: 750.0, status: "Pending" },
  { id: "INV-008", customerName: "Wayne Enterprises", invoiceDate: "2025-09-10", dueDate: "2025-09-25", grandTotal: 5000.0, status: "Paid" },
  { id: "INV-009", customerName: "Cyberdyne Systems", invoiceDate: "2025-08-20", dueDate: "2025-09-04", grandTotal: 850.0, status: "Overdue" },
  { id: "INV-010", customerName: "Weyland-Yutani", invoiceDate: "2025-12-10", dueDate: "2025-12-25", grandTotal: 1800.0, status: "Pending" },
  { id: "INV-011", customerName: "Initech", invoiceDate: "2025-07-01", dueDate: "2025-07-15", grandTotal: 2200.0, status: "Paid" },
  { id: "INV-012", customerName: "Bluth Company", invoiceDate: "2025-11-15", dueDate: "2025-11-30", grandTotal: 1000.0, status: "Overdue" },
];

const formatCurrency = (amount) => {
  return Number(amount).toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// Pagination component (internal)
const Pagination = ({ totalItems, itemsPerPage, currentPage, onPageChange }) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  if (totalItems === 0) return null;

  // visible window up to 5 pages
  let start = Math.max(1, currentPage - 2);
  let end = Math.min(totalPages, currentPage + 2);
  if (currentPage <= 3) {
    start = 1;
    end = Math.min(5, totalPages);
  } else if (currentPage + 2 >= totalPages) {
    end = totalPages;
    start = Math.max(1, totalPages - 4);
  }

  const pages = [];
  for (let i = start; i <= end; i++) pages.push(i);

  return (
    <nav aria-label="pagination">
      <ul className="pagination mb-0">
        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
          <button className="page-link" onClick={() => onPageChange(1)} disabled={currentPage === 1}>
            First
          </button>
        </li>
        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
          <button className="page-link" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}>
            <FiChevronLeft /> Prev
          </button>
        </li>

        {pages.map((p) => (
          <li key={p} className={`page-item ${currentPage === p ? "active" : ""}`}>
            <button className="page-link" onClick={() => onPageChange(p)}>
              {p}
            </button>
          </li>
        ))}

        <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
          <button className="page-link" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}>
            Next <FiChevronRight />
          </button>
        </li>
        <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
          <button className="page-link" onClick={() => onPageChange(totalPages)} disabled={currentPage === totalPages}>
            Last
          </button>
        </li>
      </ul>
    </nav>
  );
};

// --- MAIN COMPONENT ---
function QuotationConvert() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters & UI state
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [sortBy, setSortBy] = useState({ key: "invoiceDate", direction: "desc" });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Fetch mock data
  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      setInvoices(MOCK_INVOICES);
      setLoading(false);
    }, 500);
    return () => clearTimeout(t);
  }, []);

  // Memoized processed list (filter + sort)
  const processedInvoices = useMemo(() => {
    let list = [...invoices];

    // filter by search
    if (searchTerm.trim() !== "") {
      const s = searchTerm.trim().toLowerCase();
      list = list.filter(
        (i) =>
          i.id.toLowerCase().includes(s) ||
          i.customerName.toLowerCase().includes(s) ||
          String(i.grandTotal).toLowerCase().includes(s)
      );
    }

    // status filter
    if (statusFilter !== "All") list = list.filter((i) => i.status === statusFilter);

    // date range filter (invoiceDate)
    if (dateRange.start) list = list.filter((i) => i.invoiceDate >= dateRange.start);
    if (dateRange.end) list = list.filter((i) => i.invoiceDate <= dateRange.end);

    // sort
    list.sort((a, b) => {
      const aVal = a[sortBy.key];
      const bVal = b[sortBy.key];

      let cmp = 0;
      if (sortBy.key === "grandTotal") cmp = Number(aVal) - Number(bVal);
      else cmp = String(aVal).localeCompare(String(bVal));

      return sortBy.direction === "asc" ? cmp : -cmp;
    });

    return list;
  }, [invoices, searchTerm, statusFilter, dateRange, sortBy]);

  // Reset page to 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, dateRange, sortBy, itemsPerPage]);

  // pagination calculations
  const totalItems = processedInvoices.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const paginated = processedInvoices.slice(startIndex, endIndex);

  // KPIs
  const kpis = useMemo(() => {
    const total = invoices.length;
    const totalPaid = invoices.filter((i) => i.status === "Paid").length;
    const totalOverdueAmount = invoices.filter((i) => i.status === "Overdue").reduce((s, i) => s + i.grandTotal, 0);
    return { total, totalPaid, totalOverdueAmount: formatCurrency(totalOverdueAmount) };
  }, [invoices]);

  // Sorting handler
  const handleSort = (key) => {
    setSortBy((prev) => {
      if (prev.key === key) return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      return { key, direction: "desc" };
    });
  };

  const SortIcon = ({ columnKey }) => {
    if (sortBy.key !== columnKey) return null;
    return sortBy.direction === "asc" ? <FiChevronsUp size={14} className="ms-1 text-muted" /> : <FiChevronsDown size={14} className="ms-1 text-muted" />;
  };

  const getStatusBadge = (status) => {
    let cls = "bg-secondary";
    if (status === "Paid") cls = "bg-success";
    else if (status === "Overdue") cls = "bg-danger";
    else if (status === "Pending") cls = "bg-warning text-dark";
    else if (status === "Draft") cls = "bg-info text-dark";
    return <span className={`badge ${cls} status-badge`}>{status}</span>;
  };

  // actions (stubs)
  const handleViewInvoice = (id) => {
    // wire up to navigation or modal in your app
    alert(`View invoice ${id}`);
  };
  const handleCreateNew = () => {
    alert("Go to create invoice page");
  };

  return (
    <>
      <Topbar />
      <Sidebar />

 
      <main className="main-wrapper">
        <div className="page-inner">
 
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>

                <h2 className="mb-2 d-flex align-items-center">
                            <FiFileText className="me-2 " />All Converted Quotatios / Estimates
                        </h2>
              {/* <h3 className="mb-0 d-flex align-items-center" style={{ gap: 10 }}>
                <FiDollarSign size={26} className="text-primary" /> <span>All Invoices</span>
              </h3> */}
              <small className="text-muted">Manage invoices, track payments & overdue amounts</small>
            </div>

            <div className="d-flex align-items-center" style={{ gap: 12 }}>
              <button className="btn btn-outline-secondary d-none d-sm-inline-flex align-items-center" onClick={() => { setSearchTerm(""); setStatusFilter("All"); setDateRange({ start: "", end: "" }); }}>
                Reset Filters
              </button>
              <button className="btn btn-primary btn-pill d-flex align-items-center" onClick={handleCreateNew}>
                <FiPlus className="me-2" /> Create Invoice
              </button>
            </div>
          </div>

          {/* KPI Row */}
          {/* <div className="row g-3 mb-4">
            <div className="col-md-4">
              <div className="card card-ghost kpi-small">
                <div className="d-flex align-items-center">
                  <div className="me-3 p-2 bg-light rounded">
                    <FiList size={20} className="text-primary" />
                  </div>
                  <div>
                    <small className="text-muted">Total Invoices</small>
                    <div className="h5 mb-0">{kpis.total}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="card card-ghost kpi-small">
                <div className="d-flex align-items-center">
                  <div className="me-3 p-2 bg-light rounded">
                    <FiCreditCard size={20} className="text-success" />
                  </div>
                  <div>
                    <small className="text-muted">Paid Invoices</small>
                    <div className="h5 mb-0">{kpis.totalPaid}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="card card-ghost kpi-small">
                <div className="d-flex align-items-center">
                  <div className="me-3 p-2 bg-light rounded">
                    <FiAlertTriangle size={20} className="text-danger" />
                  </div>
                  <div>
                    <small className="text-muted">Overdue Amount</small>
                    <div className="h5 mb-0 text-danger">{kpis.totalOverdueAmount}</div>
                  </div>
                </div>
              </div>
            </div>
          </div> */}

          {/* Main Card */}
          <div className="card card-ghost p-0">
            {/* Filter bar */}
            <div className="p-3  " style={{ gap: 12 }}>
              <div className="d-flex align-items-center justify-content-between" >
                <div className="d-flex align-items-center ">
                  <small className="text-muted me-2">Show</small>
                  <select className="form-select form-select-sm" style={{ width: 80 }} value={itemsPerPage} onChange={(e) => setItemsPerPage(Number(e.target.value))}>
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={MOCK_INVOICES.length}>All</option>
                  </select>
                </div>

                <div className="input-group search-input" style={{ width: 300 }}> 
                  <input type="text" className="form-control form-control-sm" placeholder="Search ID, customer or amount..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                <span className="input-group-text"><FiSearch /></span>
                </div>
              </div>

              
            </div>

            {/* Table */}
            <div className=" p-2">
              {loading ? (
                <div className="p-5 text-center">
                  <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
                  <div className="mt-2 text-muted">Loading invoices...</div>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table mb-0 ">
                    <thead className=" ">
                      <tr>
                        <th style={{ width: "12%" }} onClick={() => handleSort("id")} className="cursor-pointer">Quotation ID <SortIcon columnKey="id" /></th>
                        <th style={{ width: "28%" }} onClick={() => handleSort("customerName")} className="cursor-pointer">Customer <SortIcon columnKey="customerName" /></th>
                        <th style={{ width: "14%" }} onClick={() => handleSort("invoiceDate")} className="cursor-pointer">Quotation Date <SortIcon columnKey="invoiceDate" /></th>
                        <th style={{ width: "14%" }} onClick={() => handleSort("dueDate")} className="cursor-pointer">Due Date <SortIcon columnKey="dueDate" /></th>
                        <th style={{ width: "12%", textAlign: "right" }} onClick={() => handleSort("grandTotal")} className="cursor-pointer">Amount <SortIcon columnKey="grandTotal" /></th>
                        <th style={{ width: "12%" }}>Status</th>
                        <th style={{ width: "8%" }}>Actions</th>
                      </tr>
                    </thead>

                    <tbody>
                      {paginated.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="text-center p-5 text-muted">
                            <div style={{ fontSize: 28, marginBottom: 8 }}>🔍</div>
                            No invoices found. Try adjusting filters or creating a new invoice.
                          </td>
                        </tr>
                      ) : (
                        paginated.map((inv) => (
                          <tr key={inv.id}>
                            <td><strong>{inv.id}</strong></td>
                            <td>
                              <div className="d-flex align-items-center">
                                <div style={{ width: 36, height: 36, borderRadius: 8, background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", marginRight: 10 }}>
                                  <FiUser className="text-muted" />
                                </div>
                                <div>
                                  <div className="fw-bold">{inv.customerName}</div>
                                  <small className="text-muted">Invoice: {inv.id}</small>
                                </div>
                              </div>
                            </td>
                            <td>{inv.invoiceDate}</td>
                            <td className={inv.status === "Overdue" ? "text-danger fw-bold" : ""}>{inv.dueDate}</td>
                            <td className="text-end text-success fw-bold">{formatCurrency(inv.grandTotal)}</td>
                            <td>{getStatusBadge(inv.status)}</td>
                            <td>
                              <div className="d-flex align-items-center" style={{ gap: 8 }}>
                                <button className="btn btn-sm btn-outline-info" title="View" onClick={() => handleViewInvoice(inv.id)}><FiEye /></button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Footer / Pagination */}
            <div className="card-footer d-flex justify-content-between align-items-center">
              <div className="text-muted">
                Showing {totalItems === 0 ? 0 : startIndex + 1} to {endIndex} of {totalItems} entries
              </div>
              <Pagination totalItems={totalItems} itemsPerPage={itemsPerPage} currentPage={currentPage} onPageChange={(p) => setCurrentPage(Math.max(1, Math.min(p, Math.max(1, Math.ceil(totalItems / itemsPerPage)))))} />
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

export default QuotationConvert;
